import React, { useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';
import { NumberInput } from 'components/ucl';
import { logButtonClick, logRBIEvent } from 'state/amplitude';
import { ClickEventComponentNames, CustomEventNames, EventTypes } from 'state/amplitude/constants';
import { selectors, useAppSelector } from 'state/global-state';
import { MAX_CART_QUANTITY, MIN_CART_QUANTITY } from 'utils/cart';
import { HapticsImpactStyle } from 'utils/haptic/types';
import { routes } from 'utils/routing';

import { useMinimalCart } from '../../../../../features/cart/hooks/use-minimal-cart';
import { EntryTypeEnum } from '../../../../../generated/graphql-gateway';
import { useCart } from '../../../../../hooks/use-cart';
import { useProductWizardContext } from '../../product-wizard';
import { useAddToCart } from '../../product-wizard/use-add-to-cart';
import { useProductTotalPrice } from '../../product-wizard/use-product-total-price';
import { useProductWizardFlow } from '../../product-wizard/use-product-wizard-flow';

import { displayValueStyleProps, incrementorStyleProps } from './styled';

export const AddToCartCta = () => {
  const { formatMessage } = useIntl();
  const { replace } = useRouter();
  const { cartEntries } = useCart();
  const { cartEntries: minimalCartEntries } = useMinimalCart();

  const {
    selectedProduct,
    slotsSelections,
    productQuantity,
    setProductQuantity,
  } = useProductWizardContext();
  const { cartIdEditing, closeWizard, rewardBenefitId, offerId, id } = useProductWizardFlow();
  const { isTakeoverActive } = useAppTakeoverPdp(id);
  const addToCart = useAddToCart();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);

  const limitPerOrder =
    selectedProduct.type === EntryTypeEnum.COMBO || selectedProduct.type === EntryTypeEnum.ITEM
      ? selectedProduct.quantity?.max ?? MAX_CART_QUANTITY
      : MAX_CART_QUANTITY;

  const quantityOnHydratedCart = cartEntries.reduce(
    (sum, cartEntry) => (cartEntry._id === selectedProduct.id ? sum + cartEntry.quantity : sum),
    0
  );

  const quantityOnMinimalCart = minimalCartEntries.reduce(
    (sum, cartEntry) => (cartEntry.id === selectedProduct.id ? sum + cartEntry.quantity : sum),
    0
  );

  const availableCartQuantity = limitPerOrder - quantityOnHydratedCart - quantityOnMinimalCart;
  const maxCartQuantityMet = productQuantity > availableCartQuantity;

  const { formattedTotalPrice } = useProductTotalPrice(
    selectedProduct,
    slotsSelections,
    productQuantity,
    cartIdEditing,
    rewardBenefitId
  );

  const buttonLabel = useMemo(() => {
    if (maxCartQuantityMet) {
      return formatMessage({ id: 'limitReached' });
    }

    const addToOrderText = formatMessage({
      id: cartIdEditing ? 'update' : 'addToCart',
    });
    return `${addToOrderText} - ${formattedTotalPrice}`;
  }, [cartIdEditing, formatMessage, formattedTotalPrice, maxCartQuantityMet]);

  const handleAddToCart = () => {
    addToCart(selectedProduct, productQuantity, slotsSelections);
    if (cartIdEditing) {
      closeWizard();
    } else {
      replace(routes.menu);
    }
  };

  const handleQuantityUpdate = (value: number) => {
    setProductQuantity((prevValue: number) => {
      const action = prevValue < value ? 'increase' : 'decrease';
      logButtonClick(
        {
          attributes: {
            component: ClickEventComponentNames.BUTTON,
            name: 'Product Incrementor',
          },
        },
        { logDuplicateClickEvent: true }
      );
      logRBIEvent(
        {
          name: CustomEventNames.ITEM_QUANTITY_MODIFIED,
          type: EventTypes.Other,
          attributes: {
            increaseOrDecrease: action,
            totalQuantity: value,
          },
        },
        { skipLoggingToBraze: true }
      );
      return value;
    });
  };

  const isDisabled = maxCartQuantityMet;
  // FIXME: disallow changing quantity for rewards incentives - CartV2 and Hydration can't handle it yet
  const isEditingCartEntryWithReward = appliedLoyaltyRewards[cartIdEditing ?? ''];

  const isOneMenuComboReward = !!rewardBenefitId && selectedProduct.type === EntryTypeEnum.COMBO;

  const showIncrementor =
    !maxCartQuantityMet && !isEditingCartEntryWithReward && !offerId && !isOneMenuComboReward;

  const disableIncrementQuantity = maxCartQuantityMet;
  const disableIncrementor = maxCartQuantityMet;

  const productName = selectedProduct.name?.locale ?? '';
  const ariaLabels = useMemo(
    () => ({
      ariaLabel: formatMessage(
        { id: cartIdEditing ? 'updateQuantityAndItemWithPrice' : 'addQuantityAndItemWithPrice' },
        { itemName: productName, quantity: productQuantity, price: formattedTotalPrice }
      ),
      decrementAriaLabel: formatMessage({ id: 'decrementAriaLabel' }, { productName }),
      incrementAriaLabel: formatMessage({ id: 'incrementAriaLabel' }, { productName }),
      quantityAriaLabel: formatMessage(
        { id: 'quantityAriaLabel' },
        { productQuantity, productName }
      ),
    }),
    [cartIdEditing, formatMessage, formattedTotalPrice, productName, productQuantity]
  );

  return (
    <>
      {showIncrementor && (
        <NumberInput
          displayedValue={productQuantity}
          value={productQuantity}
          onChange={handleQuantityUpdate}
          min={MIN_CART_QUANTITY}
          max={availableCartQuantity}
          hideNumberInput
          disableIncrement={disableIncrementQuantity}
          disabled={disableIncrementor}
          ariaLabel={ariaLabels.quantityAriaLabel}
          decrementAriaLabel={ariaLabels.decrementAriaLabel}
          incrementAriaLabel={ariaLabels.incrementAriaLabel}
          displayValueStyleProps={displayValueStyleProps}
          incrementorStyleProps={incrementorStyleProps}
          hapticFeedbackStyle={HapticsImpactStyle.Light}
        />
      )}
      <ActionButton
        accessibilityLabel={ariaLabels.ariaLabel}
        testID="product-home-action-button"
        disabled={isDisabled}
        onPress={handleAddToCart}
        flex={1}
        marginLeft={showIncrementor ? '$6' : '0'}
        variant={
          isTakeoverActive ? ActionButtonVariants.PRIMARY_TAKEOVER : ActionButtonVariants.PRIMARY
        }
      >
        {buttonLabel}
      </ActionButton>
    </>
  );
};
